/**
 * F09 v2 — typed client for every admin verification endpoint (plan §6).
 *
 * Envelopes: reads return `{ data }` (lists add `meta.pagination`); errors are
 * `{ error: { code, message, details? } }` and surface as `ApiError`.
 *
 * Requests (`/admin/verifications`)
 *   GET  /admin/verifications                                → queue (filters + sort)
 *   GET  /admin/verifications/count?status=                  → authoritative count
 *   GET  /admin/verifications/:id                            → request detail
 *   GET  /admin/verifications/:id/documents/:docId/content   → document bytes (E7 proxy)
 *   GET  /admin/verifications/:id/access-log                 → document access log
 *   POST /admin/verifications/:id/approve | reject | request-more-info
 *
 * Entities (`/admin/verification-entities`)
 *   GET  /admin/verification-entities?ownerUserId=           → owner's entities (E14)
 *   GET  /admin/verification-entities/:type/:id              → entity profile
 *   POST /admin/verification-entities/:type/:id/revoke | lift-cooldown | unlock-rerequest
 *   POST /admin/verification-entities/:type/:id/direct-verify  (issue_privileged)
 *
 * Candidates / watchlist / QA
 *   GET  /admin/verification-candidates
 *   POST /admin/verification-candidates/:id/invite | dismiss
 *   GET  /admin/verification-watchlist
 *   POST /admin/verification-watchlist/:id/resolve
 *   GET  /admin/verification-qa
 *
 * The ID number in approve / direct-verify bodies is sent once over HTTPS and
 * never logged, cached or stored by this client.
 *
 * Tests swap this module for `verification.fixture.ts` via the vitest alias.
 */
import type {
  AdminApproveVerificationInput,
  AdminDirectVerifyInput,
  AdminDismissCandidateInput,
  AdminEntityVerificationProfile,
  AdminInviteCandidateInput,
  AdminLiftCooldownInput,
  AdminPageMeta,
  AdminRejectVerificationInput,
  AdminRequestMoreInfoInput,
  AdminResolveWatchlistInput,
  AdminRevokeVerificationInput,
  AdminUnlockRerequestInput,
  AdminVerificationQueueItem,
  AdminVerificationRequestDetail,
  CandidateStatus,
  EntityVerificationSummary,
  VerifiableEntityType,
  VerificationAccessLogRow,
  VerificationCandidateView,
  VerificationQaPayload,
  VerificationQueueSort,
  VerificationRequestKind,
  VerificationRequestStatus,
  VerificationStrength,
  WatchlistReasonCode,
  WatchlistStatus,
} from '@bravemove/types';
import { adminFetch, adminFetchBlob } from '../auth/apiClient';
import { buildVerificationQuery } from '../lib/verificationQuery';

export interface VerificationQueueQuery {
  readonly status?: readonly VerificationRequestStatus[] | undefined;
  readonly entityType?: VerifiableEntityType | undefined;
  readonly requestKind?: VerificationRequestKind | undefined;
  readonly strength?: VerificationStrength | undefined;
  readonly hasDuplicateFlag?: boolean | undefined;
  readonly countryCode?: string | undefined;
  readonly sort?: VerificationQueueSort | undefined;
  readonly page?: number | undefined;
  readonly perPage?: number | undefined;
}

export interface VerificationCandidatesQuery {
  readonly status?: readonly CandidateStatus[] | undefined;
  readonly entityType?: VerifiableEntityType | undefined;
  readonly page?: number | undefined;
  readonly perPage?: number | undefined;
}

export interface VerificationWatchlistQuery {
  readonly status?: readonly WatchlistStatus[] | undefined;
  readonly entityType?: VerifiableEntityType | undefined;
  readonly reasonCode?: WatchlistReasonCode | undefined;
  readonly page?: number | undefined;
  readonly perPage?: number | undefined;
}

/** A page of rows + its page-numbered meta (null when the API omits it). */
export interface VerificationListPage<T> {
  readonly items: readonly T[];
  readonly pagination: AdminPageMeta | null;
}

/** Raw document bytes from the E7 proxy. */
export interface VerificationDocumentContent {
  readonly blob: Blob;
  readonly contentType: string;
}

interface ListEnvelope<T> {
  readonly data?: readonly T[];
  readonly meta?: { readonly pagination?: AdminPageMeta };
  readonly pagination?: AdminPageMeta;
}

function toPage<T>(res: ListEnvelope<T> | null): VerificationListPage<T> {
  return {
    items: res?.data ?? [],
    pagination: res?.meta?.pagination ?? res?.pagination ?? null,
  };
}

function entityPath(type: VerifiableEntityType, id: string): string {
  return `/admin/verification-entities/${encodeURIComponent(type)}/${encodeURIComponent(id)}`;
}

export const adminVerificationApi = {
  // ─── Requests ──────────────────────────────────────────────────────────────
  list: async (
    query: VerificationQueueQuery = {},
  ): Promise<VerificationListPage<AdminVerificationQueueItem>> => {
    const res = await adminFetch<ListEnvelope<AdminVerificationQueueItem>>(
      `/admin/verifications${buildVerificationQuery({
        status: query.status,
        entityType: query.entityType,
        requestKind: query.requestKind,
        strength: query.strength,
        hasDuplicateFlag: query.hasDuplicateFlag,
        countryCode: query.countryCode,
        sort: query.sort,
        page: query.page,
        perPage: query.perPage,
      })}`,
    );
    return toPage(res);
  },

  detail: async (id: string): Promise<AdminVerificationRequestDetail> => {
    const res = await adminFetch<{ readonly data: AdminVerificationRequestDetail }>(
      `/admin/verifications/${encodeURIComponent(id)}`,
    );
    return res.data;
  },

  /** E7 — fetch a document through the authenticated proxy (`doc.contentPath`). */
  fetchDocumentContent: (contentPath: string): Promise<VerificationDocumentContent> =>
    adminFetchBlob(contentPath),

  accessLog: async (id: string): Promise<readonly VerificationAccessLogRow[]> => {
    const res = await adminFetch<{ readonly data?: readonly VerificationAccessLogRow[] }>(
      `/admin/verifications/${encodeURIComponent(id)}/access-log`,
    );
    return res?.data ?? [];
  },

  approve: (id: string, body: AdminApproveVerificationInput): Promise<unknown> =>
    adminFetch(`/admin/verifications/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: { ...body },
    }),

  reject: (id: string, body: AdminRejectVerificationInput): Promise<unknown> =>
    adminFetch(`/admin/verifications/${encodeURIComponent(id)}/reject`, {
      method: 'POST',
      body: { ...body },
    }),

  requestMoreInfo: (id: string, body: AdminRequestMoreInfoInput): Promise<unknown> =>
    adminFetch(`/admin/verifications/${encodeURIComponent(id)}/request-more-info`, {
      method: 'POST',
      body: { ...body },
    }),

  // ─── Entities ──────────────────────────────────────────────────────────────
  entitiesForOwner: async (ownerUserId: string): Promise<readonly EntityVerificationSummary[]> => {
    const res = await adminFetch<{ readonly data?: readonly EntityVerificationSummary[] }>(
      `/admin/verification-entities${buildVerificationQuery({ ownerUserId })}`,
    );
    return res?.data ?? [];
  },

  entityProfile: async (
    type: VerifiableEntityType,
    id: string,
  ): Promise<AdminEntityVerificationProfile> => {
    const res = await adminFetch<{ readonly data: AdminEntityVerificationProfile }>(
      entityPath(type, id),
    );
    return res.data;
  },

  revoke: (
    type: VerifiableEntityType,
    id: string,
    body: AdminRevokeVerificationInput,
  ): Promise<unknown> =>
    adminFetch(`${entityPath(type, id)}/revoke`, { method: 'POST', body: { ...body } }),

  liftCooldown: (
    type: VerifiableEntityType,
    id: string,
    body: AdminLiftCooldownInput,
  ): Promise<unknown> =>
    adminFetch(`${entityPath(type, id)}/lift-cooldown`, { method: 'POST', body: { ...body } }),

  unlockRerequest: (
    type: VerifiableEntityType,
    id: string,
    body: AdminUnlockRerequestInput,
  ): Promise<unknown> =>
    adminFetch(`${entityPath(type, id)}/unlock-rerequest`, { method: 'POST', body: { ...body } }),

  directVerify: (
    type: VerifiableEntityType,
    id: string,
    body: AdminDirectVerifyInput,
  ): Promise<unknown> =>
    adminFetch(`${entityPath(type, id)}/direct-verify`, { method: 'POST', body: { ...body } }),

  // ─── Candidates ────────────────────────────────────────────────────────────
  candidates: async (
    query: VerificationCandidatesQuery = {},
  ): Promise<VerificationListPage<VerificationCandidateView>> => {
    const res = await adminFetch<ListEnvelope<VerificationCandidateView>>(
      `/admin/verification-candidates${buildVerificationQuery({
        status: query.status,
        entityType: query.entityType,
        page: query.page,
        perPage: query.perPage,
      })}`,
    );
    return toPage(res);
  },

  inviteCandidate: (id: string, body: AdminInviteCandidateInput): Promise<unknown> =>
    adminFetch(`/admin/verification-candidates/${encodeURIComponent(id)}/invite`, {
      method: 'POST',
      body: { ...body },
    }),

  dismissCandidate: (id: string, body: AdminDismissCandidateInput): Promise<unknown> =>
    adminFetch(`/admin/verification-candidates/${encodeURIComponent(id)}/dismiss`, {
      method: 'POST',
      body: { ...body },
    }),

  // ─── Watchlist ─────────────────────────────────────────────────────────────
  watchlist: async (
    query: VerificationWatchlistQuery = {},
  ): Promise<VerificationListPage<VerificationCandidateView>> => {
    const res = await adminFetch<ListEnvelope<VerificationCandidateView>>(
      `/admin/verification-watchlist${buildVerificationQuery({
        status: query.status,
        entityType: query.entityType,
        reasonCode: query.reasonCode,
        page: query.page,
        perPage: query.perPage,
      })}`,
    );
    return toPage(res);
  },

  resolveWatchlist: (id: string, body: AdminResolveWatchlistInput): Promise<unknown> =>
    adminFetch(`/admin/verification-watchlist/${encodeURIComponent(id)}/resolve`, {
      method: 'POST',
      body: { ...body },
    }),

  // ─── QA ────────────────────────────────────────────────────────────────────
  qa: async (): Promise<VerificationQaPayload> => {
    const res = await adminFetch<{ readonly data: VerificationQaPayload }>(
      '/admin/verification-qa',
    );
    return res.data;
  },
};

export type AdminVerificationApi = typeof adminVerificationApi;
